/**
 * 租戶設定作業（Task-tenant-provisioning PR F）
 * func_code: tenant_settings（is_mana，純系統管理者）
 * 12 欄位設定 form + 進階操作（產 Traefik / 立即上線 / DNS 同步 / 郵件開通）。
 * i18n：t(key, fallback) — PR H inject sys_languages 後自動改用 DB 翻譯。
 * ⚠️ Logo / 登入底圖上傳待 tenant file_object owner_org_id 擴展（另 PR），本頁先停用。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Divider, FormControl,
  FormControlLabel, FormGroup, FormLabel, Grid, InputLabel, MenuItem, Paper, Radio,
  RadioGroup, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { usePermission } from '../hooks/usePermission';
import * as tenantService from '../services/tenantService';
import { recheckTenantOfficeHealth, OfficeHealthRecheck } from '../services/officeAdminService';
import { TenantSettings } from '../services/tenantService';
import { getUserRoles, UserRole } from '../services/userRoleService';
import { getI18nValue } from '../utils/i18nHelper';

const FUNC_CODE = 'tenant_settings';
const LANGS = ['zh-TW', 'en', 'zh-CN'];
const CHANNELS = ['web', 'pwa', 'line', 'native', 'voice', 'api', 'email'];
const TENANT_TYPES = ['management', 'business', 'partner', 'demo', 'beta'];
const POLICY_FACTORS: Record<string, string[]> = {
  L01: ['credential'],
  L02: ['credential', 'captcha'],
  L03: ['credential', 'totp'],
};

const TenantSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();
  const canRead = hasPermission(FUNC_CODE, 'read');
  const canUpdate = hasPermission(FUNC_CODE, 'update');

  const [tenants, setTenants] = useState<tenantService.TenantListItem[]>([]);
  const [orgId, setOrgId] = useState<number | ''>('');
  const [form, setForm] = useState<TenantSettings | null>(null);
  const [allRoles, setAllRoles] = useState<UserRole[]>([]);   // 排 role=1（系統管理員不授權給租戶）
  const [mailDomains, setMailDomains] = useState<tenantService.MailDomainItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 4-pre-c：租戶專屬 AI Provider 設定已移除（統一走 /ee_ai_providers + 訂閱授權）；保留 AI 用量帳單。
  const [billingMonth, setBillingMonth] = useState('');
  // 3.2e Office 服務健康檢查（依所選租戶 · 走 SYSTEM_ADMIN recheck，非本人 org self-check）
  const [officeHealth, setOfficeHealth] = useState<OfficeHealthRecheck | null>(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [showOfficeChecks, setShowOfficeChecks] = useState(false);

  const tt = (k: string, fallback: string) => t(`tenantSettings.${k}`, fallback);

  // Office toggle ON（且已選租戶）→ auto 跑該租戶健康檢查
  useEffect(() => {
    if (!orgId || !form?.office_enabled) { setOfficeHealth(null); return; }
    let alive = true;
    setCheckingHealth(true);
    recheckTenantOfficeHealth(Number(orgId))
      .then((h) => { if (alive) setOfficeHealth(h); })
      .catch(() => { if (alive) setOfficeHealth({ org_id: Number(orgId), office_enabled: true, overall_status: 'error', checks: [] }); })
      .finally(() => { if (alive) setCheckingHealth(false); });
    return () => { alive = false; };
  }, [orgId, form?.office_enabled]);

  useEffect(() => {
    if (permLoading || !canRead) return;
    (async () => {
      try {
        const [ts, md, roles] = await Promise.all([
          tenantService.listTenants(),
          tenantService.listMailDomains(),
          getUserRoles(),
        ]);
        setTenants(ts);
        setMailDomains(md);
        setAllRoles(roles.filter((r) => r.id !== 1));   // role=1 系統管理員不列
        if (ts.length > 0) setOrgId(ts[0].org_id);
      } catch (err) {
        showApiError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading, canRead]);

  const loadSettings = useCallback(async (id: number) => {
    setLoading(true);
    try {
      setForm(await tenantService.getSettings(id));
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof orgId === 'number') loadSettings(orgId);
  }, [orgId, loadSettings]);

  // 4-pre-c：loadAi / setAiField / handleSaveAi 已移除（租戶 AI Provider 設定改走 /ee_ai_providers）。

  const handleDownloadBilling = async () => {
    if (typeof orgId !== 'number' || !billingMonth) return;
    try {
      const blob = await tenantService.downloadAiBilling(orgId, billingMonth);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai_billing_${orgId}_${billingMonth}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showApiError(err);
    }
  };

  const setField = <K extends keyof TenantSettings>(k: K, v: TenantSettings[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const toggleInList = <T,>(list: T[], v: T): T[] =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const handleSave = async () => {
    if (!form || typeof orgId !== 'number') return;
    setSaving(true);
    try {
      const updated = await tenantService.updateSettings(orgId, {
        primary_domain: form.primary_domain || undefined,
        alias_domains: form.alias_domains,
        tenant_type: form.tenant_type,
        tenant_channels: form.tenant_channels,
        storage_quota_gb: form.storage_quota_gb,
        home_index_path: form.home_index_path || undefined,
        mail_domain: form.mail_domain || undefined,
        tenant_title: form.tenant_title,
        tenant_copyright: form.tenant_copyright,
        tenant_languages: form.tenant_languages,
        allowed_role_ids: form.allowed_role_ids,
        is_service_active: form.is_service_active,
        office_enabled: form.office_enabled,
        login_policy_code: form.login_policy.policy_code,
        login_factors: form.login_policy.factors,
      });
      setForm(updated);
      showSuccess('SYS350001', { name: tt('title', '租戶設定作業') });
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn: () => Promise<any>, code: string) => {
    try {
      const res = await fn();
      // lint-msg-allow: code 由呼叫端傳入固定 9 碼字面量（SYS350002~350005）
      showSuccess(code, { name: res?.status ?? 'ok' });
    } catch (err) {
      showApiError(err);
    }
  };

  if (!permLoading && !canRead) {
    return <Box sx={{ p: 3 }}><Alert severity="warning">{t('common.noPermission', '無權限')}</Alert></Box>;
  }

  const mlField = (label: string, field: 'tenant_title' | 'tenant_copyright') => (
    <Box>
      <FormLabel>{label}</FormLabel>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {LANGS.map((lng) => (
          <TextField
            key={lng}
            size="small"
            label={lng}
            value={form?.[field]?.[lng] ?? ''}
            onChange={(e) => form && setField(field, { ...form[field], [lng]: e.target.value })}
          />
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>{tt('title', '租戶設定作業')}</Typography>

      <FormControl size="small" sx={{ minWidth: 320, mb: 2 }}>
        <InputLabel>{tt('selectTenant', '選擇租戶')}</InputLabel>
        <Select
          label={tt('selectTenant', '選擇租戶')}
          value={orgId}
          onChange={(e) => setOrgId(Number(e.target.value))}
        >
          {tenants.map((tn) => (
            <MenuItem key={tn.org_id} value={tn.org_id}>
              {tn.org_name}（{tn.org_code}）{tn.primary_domain ? ` · ${tn.primary_domain}` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading || !form ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={2}>
          {/* 租戶基本 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupBasic', '租戶基本')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={tt('primaryDomain', '服務網址')}
                  value={form.primary_domain ?? ''} onChange={(e) => setField('primary_domain', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={tt('aliasDomains', '別名網域（逗號分隔）')}
                  value={form.alias_domains.join(', ')}
                  onChange={(e) => setField('alias_domains', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('tenantType', '租戶類型')}</InputLabel>
                  <Select label={tt('tenantType', '租戶類型')} value={form.tenant_type}
                    onChange={(e) => setField('tenant_type', e.target.value)}>
                    {TENANT_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormLabel>{tt('channels', '啟用管道')}</FormLabel>
                <FormGroup row>
                  {CHANNELS.map((ch) => (
                    <FormControlLabel key={ch} label={ch}
                      control={<Checkbox checked={form.tenant_channels.includes(ch)}
                        onChange={() => setField('tenant_channels', toggleInList(form.tenant_channels, ch))} />} />
                  ))}
                </FormGroup>
              </Grid>
              <Grid size={12}>
                <FormLabel>{tt('allowedRoles', '允許角色（授權此租戶可指派的角色）')}</FormLabel>
                <FormGroup row>
                  {allRoles.map((r) => (
                    <FormControlLabel key={r.id} label={getI18nValue(r.role_name, i18n.language)}
                      control={<Checkbox checked={(form.allowed_role_ids || []).includes(r.id)}
                        onChange={() => setField('allowed_role_ids', toggleInList(form.allowed_role_ids || [], r.id))} />} />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>

          {/* 品牌 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupBrand', '品牌')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>{mlField(tt('browserTitle', '瀏覽器標題'), 'tenant_title')}</Grid>
              <Grid size={{ xs: 12, sm: 6 }}>{mlField(tt('copyright', '版權宣告'), 'tenant_copyright')}</Grid>
              <Grid size={12}>
                <Alert severity="info">
                  {tt('logoDeferred', 'Logo / 登入底圖上傳待租戶檔案（file_object owner）擴展後開放')}
                  {form.logo_file_id ? `（logo_file_id=${form.logo_file_id}）` : ''}
                </Alert>
              </Grid>
            </Grid>
          </Paper>

          {/* 存取 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupAccess', '存取')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl>
                  <FormLabel>{tt('loginPolicy', '登入政策')}</FormLabel>
                  <RadioGroup row value={form.login_policy.policy_code}
                    onChange={(e) => setField('login_policy',
                      { policy_code: e.target.value, factors: POLICY_FACTORS[e.target.value] || form.login_policy.factors })}>
                    <FormControlLabel value="L01" control={<Radio />} label="L01 帳密" />
                    <FormControlLabel value="L02" control={<Radio />} label="L02 帳密+驗證碼" />
                    <FormControlLabel value="L03" control={<Radio />} label="L03 帳密+2FA" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField fullWidth size="small" type="number" label={tt('quota', '總空間量 (GB)')}
                  value={form.storage_quota_gb}
                  onChange={(e) => setField('storage_quota_gb', Number(e.target.value))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 9 }}>
                <TextField fullWidth size="small" label={tt('homeIndex', '首頁位置（空=預設 home/<id>/index.html）')}
                  value={form.home_index_path ?? ''} onChange={(e) => setField('home_index_path', e.target.value)} />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={<Switch color="success" checked={form.is_service_active}
                    onChange={(e) => setField('is_service_active', e.target.checked)} />}
                  label={
                    <Box>
                      <strong>{tt('serviceActive', '系統服務啟動')}</strong>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {form.is_service_active
                          ? tt('serviceActiveHint', '對租戶開放服務 · 用戶正常登入')
                          : tt('serviceInactiveHint', '對租戶暫停服務 · 用戶顯示靜態暫停頁')}
                      </Typography>
                    </Box>
                  } />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={<Switch color="success" checked={form.office_enabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // 啟用需二次確認（Task-33 · 未來測完加購的服務）
                      if (on && !window.confirm(
                        tt('officeConfirm', '啟用後，該租戶使用者可在我的網路硬碟編輯 Office 檔案。是否確認？'))) return;
                      setField('office_enabled', on);
                    }} />}
                  label={
                    <Box>
                      <strong>{tt('officeEnabled', 'Office 服務（線上編輯）')}</strong>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {form.office_enabled
                          ? tt('officeEnabledHint', '已啟用 · 該租戶可在我的網路硬碟線上編輯 Office 檔案')
                          : tt('officeDisabledHint', '未啟用（預設）· 我的網路硬碟不顯示「線上編輯」')}
                      </Typography>
                    </Box>
                  } />
                {/* 3.2e Office 服務健康指示（toggle ON 時 auto 檢查） */}
                {form.office_enabled && (
                  <Box sx={{ ml: 6, mt: 0.5 }}>
                    {checkingHealth ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                          {tt('officeHealthChecking', 'Office 服務健康檢查中…')}
                        </Typography>
                      </Stack>
                    ) : officeHealth && (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small"
                            color={officeHealth.overall_status === 'ok' ? 'success'
                              : officeHealth.overall_status === 'warn' ? 'warning' : 'error'}
                            label={officeHealth.overall_status === 'ok' ? tt('officeHealthOK', '✅ Office 服務正常')
                              : officeHealth.overall_status === 'warn' ? tt('officeHealthWarn', '⚠️ Office 服務部分問題')
                              : tt('officeHealthError', '❌ Office 服務啟動失敗')} />
                          {officeHealth.checks.length > 0 && (
                            <Button size="small" variant="text" onClick={() => setShowOfficeChecks((v) => !v)}>
                              {showOfficeChecks ? tt('officeHealthHide', '隱藏檢查項') : tt('officeHealthShow', '檢查明細')}
                            </Button>
                          )}
                        </Stack>
                        <Collapse in={showOfficeChecks}>
                          <Box sx={{ mt: 0.5 }}>
                            {officeHealth.checks.map((c) => (
                              <Box key={c.name} sx={{ mb: 0.25 }}>
                                <Typography variant="caption" display="block">
                                  {c.status === 'ok' ? '✅' : c.status === 'warn' ? '⚠️' : c.status === 'off' ? '⚪' : '❌'} {c.name}
                                  {c.latency_ms != null && ` (${c.latency_ms}ms)`}
                                </Typography>
                                {c.hint && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 2 }}>
                                    {c.hint}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* 郵件 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupMail', '郵件')}</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('mailDomain', '郵件網域（限已納管）')}</InputLabel>
              <Select label={tt('mailDomain', '郵件網域（限已納管）')} value={form.mail_domain ?? ''}
                onChange={(e) => setField('mail_domain', e.target.value)}>
                <MenuItem value=""><em>{tt('none', '（不指派）')}</em></MenuItem>
                {mailDomains.map((d) => <MenuItem key={d.domain_code} value={d.domain_code}>{d.domain_code}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          {/* AI 用量計費（4-pre-c：AI Provider 設定已移至 /ee_ai_providers + 訂閱授權，此處僅保留帳單） */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupAiBilling', 'AI 用量計費')}</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>
              {tt('aiMovedHint', 'AI Provider 設定已移至「AI Provider」頁與「服務訂閱管理」授權；此處僅提供用量帳單下載。')}
            </Alert>
            <Typography variant="body2" gutterBottom>{tt('aiBilling', '用量計費對帳單')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" type="month" label={tt('aiBillingMonth', '計費月份')}
                InputLabelProps={{ shrink: true }} value={billingMonth}
                onChange={(e) => setBillingMonth(e.target.value)} />
              <Button size="small" variant="outlined" onClick={handleDownloadBilling}
                disabled={!canRead || !billingMonth}>
                {tt('aiBillingCsv', '下載 CSV')}
              </Button>
            </Stack>
          </Paper>

          {/* 國際化 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupI18n', '國際化')}</Typography>
            <FormLabel>{tt('enabledLangs', '啟用語系')}</FormLabel>
            <FormGroup row>
              {LANGS.map((lng) => (
                <FormControlLabel key={lng} label={lng}
                  control={<Checkbox checked={form.tenant_languages.includes(lng)}
                    onChange={() => setField('tenant_languages', toggleInList(form.tenant_languages, lng))} />} />
              ))}
            </FormGroup>
          </Paper>

          <Box>
            <Button variant="contained" onClick={handleSave} disabled={!canUpdate || saving}>
              {saving ? tt('saving', '儲存中…') : tt('save', '儲存')}
            </Button>
          </Box>

          {/* 進階操作 */}
          <Divider />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{tt('groupAdvanced', '進階操作（產出 / 生效 / 同步）')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" disabled={!canUpdate}
                onClick={() => runAction(() => tenantService.generateConfig(orgId as number), 'SYS350002')}>
                {tt('genConfig', '產生 Traefik 設定')}
              </Button>
              <Button size="small" variant="outlined" disabled={!canUpdate}
                onClick={() => runAction(() => tenantService.applyConfig(orgId as number), 'SYS350003')}>
                {tt('applyNow', '立即上線')}
              </Button>
              <Button size="small" variant="outlined" disabled={!canUpdate}
                onClick={() => runAction(() => tenantService.dnsSync(orgId as number), 'SYS350004')}>
                {tt('dnsSync', 'DNS 同步')}
              </Button>
              <Button size="small" variant="outlined" disabled={!canUpdate}
                onClick={() => runAction(() => tenantService.mailProvision(orgId as number), 'SYS350005')}>
                {tt('mailProvision', '郵件開通')}
              </Button>
            </Stack>
            <Chip sx={{ mt: 1 }} size="small" label={tt('advancedHint', '排程每 15 分鐘自動 apply；急件按「立即上線」')} />
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default TenantSettingsPage;
